<template>
  <div class="stores-page">
    <van-nav-bar
      :fixed="true"
      z-index="9"
      :title="$t('storesPage.title')"
      :border="false"
      left-arrow
      @click-left="goBack"
    />

    <van-pull-refresh
      v-model="refreshing"
      :disabled="initialLoading"
      :success-duration="0"
      pulling-text=""
      loosing-text=""
      loading-text=""
      @refresh="refreshStores"
    >
      <main class="stores-page__content">
        <section v-if="initialLoading" class="page-state page-state--loading">
          <van-loading type="spinner" color="#0B45FF" size="36px" />
          <p>{{ $t('loading') }}</p>
        </section>

        <section v-else-if="loadError && stores.length === 0" class="page-state">
          <div class="page-state__icon page-state__icon--error">
            <van-icon name="warning-o" />
          </div>
          <h2>{{ $t('storesPage.loadFailed') }}</h2>
          <p>{{ loadError }}</p>
          <button type="button" class="primary-button" @click="retryLoad">
            <van-icon name="replay" />
            <span>{{ $t('storesPage.retry') }}</span>
          </button>
        </section>

        <section v-else-if="stores.length === 0" class="page-state page-state--empty">
          <img
            class="empty-illustration"
            src="@/assets/img/home-alignment/stores-empty.svg"
            alt=""
            aria-hidden="true"
          >
          <h2>{{ $t('storesPage.emptyTitle') }}</h2>
          <p>{{ $t('storesPage.emptyDesc') }}</p>
          <button type="button" class="primary-button" @click="retryLoad">
            <van-icon name="replay" />
            <span>{{ $t('storesPage.retry') }}</span>
          </button>
        </section>

        <section v-else class="store-list" :aria-label="$t('storesPage.title')">
          <article
            v-for="(store, index) in stores"
            :key="store.id || `store-${index}`"
            class="store-card"
          >
            <div class="store-card__image">
              <img
                v-if="storeImageUrl(store) && !hasImageFailed(store, index)"
                :src="storeImageUrl(store)"
                :alt="displayName(store)"
                @error="markImageFailed(store, index)"
              >
              <van-icon v-else name="photo-o" aria-hidden="true" />
            </div>

            <div class="store-card__details">
              <h2>{{ displayName(store) }}</h2>
              <span
                class="status-pill"
                :class="{ 'status-pill--open': isBusinessActive(store) }"
              >
                {{ statusText(store) }}
              </span>

              <div v-if="displayAddress(store)" class="store-info-row">
                <span class="store-info-row__icon"><van-icon name="location-o" /></span>
                <span class="store-info-row__text store-info-row__text--address">
                  {{ displayAddress(store) }}
                </span>
              </div>

              <div v-if="displayBusinessHour(store)" class="store-info-row">
                <span class="store-info-row__icon"><van-icon name="clock-o" /></span>
                <span class="store-info-row__text">{{ displayBusinessHour(store) }}</span>
              </div>

              <a
                v-if="displayPhone(store)"
                class="store-info-row store-info-row--phone"
                :href="phoneHref(store.phone)"
                @click.prevent="dialStore(store.phone)"
              >
                <span class="store-info-row__icon"><van-icon name="phone-o" /></span>
                <span class="store-info-row__text">{{ displayPhone(store) }}</span>
              </a>
            </div>
          </article>
        </section>
      </main>
    </van-pull-refresh>
  </div>
</template>

<script>
import { Icon, Loading, PullRefresh } from 'vant';
import { getStores } from '@/api/common';
import { resolveAssetUrl } from '@/common/product';
import routerBack from '@/mixins/routerBack';

const INDONESIA_COUNTRY_CODE = '62';
const INDONESIA_LEADING_ZERO = '0';
const PHONE_MINIMUM_DIGITS = 8;
const LOCAL_PHONE_MINIMUM_DIGITS = 7;
const MOBILE_PREFIX_LENGTH = 3;
const PHONE_GROUP_LENGTH = 4;

export default {
  name: 'StoreList',
  mixins: [routerBack],
  components: {
    [Icon.name]: Icon,
    [Loading.name]: Loading,
    [PullRefresh.name]: PullRefresh
  },
  data() {
    return {
      stores: [],
      initialLoading: true,
      refreshing: false,
      requesting: false,
      loadError: '',
      failedImages: {}
    };
  },
  mounted() {
    this.loadStores();
  },
  methods: {
    async loadStores({ refresh = false } = {}) {
      if (this.requesting) {
        if (refresh) this.refreshing = false;
        return;
      }

      this.requesting = true;
      if (!refresh) {
        this.initialLoading = true;
        this.loadError = '';
      }

      try {
        const res = await getStores();
        if (res?.code !== 'Success') {
          throw new Error(res?.message || this.$t('storesPage.loadFailed'));
        }

        if (res.data != null && !Array.isArray(res.data)) {
          throw new Error(this.$t('storesPage.loadFailed'));
        }

        this.stores = (res.data || []).filter(Boolean);
        this.failedImages = {};
        this.loadError = '';
      } catch (error) {
        const message = this.errorMessage(error);
        if (!refresh) {
          this.loadError = message;
        }
        this.$toast(message);
      } finally {
        this.requesting = false;
        this.initialLoading = false;
        this.refreshing = false;
      }
    },
    refreshStores() {
      this.loadStores({ refresh: true });
    },
    retryLoad() {
      this.loadStores();
    },
    errorMessage(error) {
      if (typeof error === 'string' && error.trim()) return error.trim();
      if (error?.message && error.message.trim()) return error.message.trim();
      return this.$t('storesPage.loadFailed');
    },
    displayName(store) {
      return String(store?.name || '').trim() || this.$t('storesPage.unnamed');
    },
    displayAddress(store) {
      return String(store?.address || '').trim();
    },
    displayBusinessHour(store) {
      return String(store?.businessHour || '').trim();
    },
    displayPhone(store) {
      const rawPhone = String(store?.phone || '').trim();
      if (!rawPhone) return '';

      const digits = rawPhone.replace(/\D/g, '');
      if (digits.length < PHONE_MINIMUM_DIGITS) return rawPhone;

      let localDigits = '';
      if (digits.startsWith(INDONESIA_COUNTRY_CODE)) {
        localDigits = digits.slice(INDONESIA_COUNTRY_CODE.length);
      } else if (digits.startsWith(INDONESIA_LEADING_ZERO)) {
        localDigits = digits.slice(INDONESIA_LEADING_ZERO.length);
      } else {
        return rawPhone;
      }

      if (localDigits.length < LOCAL_PHONE_MINIMUM_DIGITS) return rawPhone;

      const prefix = localDigits.slice(0, MOBILE_PREFIX_LENGTH);
      const tail = localDigits.slice(MOBILE_PREFIX_LENGTH);
      const groups = tail.match(new RegExp(`.{1,${PHONE_GROUP_LENGTH}}`, 'g')) || [];
      return `+${INDONESIA_COUNTRY_CODE} ${[prefix, ...groups].join('-')}`;
    },
    phoneHref(phone) {
      const normalized = String(phone || '').trim().replace(/[^\d+]/g, '');
      return `tel:${normalized}`;
    },
    dialStore(phone) {
      const href = this.phoneHref(phone);
      if (href === 'tel:') return;
      try {
        window.location.href = href;
      } catch (error) {
        this.$toast(this.$t('storesPage.dialFailed'));
      }
    },
    isBusinessActive(store) {
      return store?.businessStatus === true;
    },
    statusText(store) {
      return this.$t(this.isBusinessActive(store) ? 'storesPage.statusOpen' : 'storesPage.statusUpgrading');
    },
    storeImageUrl(store) {
      return resolveAssetUrl(store?.imageUrl);
    },
    storeKey(store, index) {
      return String(store?.id || `store-${index}`);
    },
    hasImageFailed(store, index) {
      return Boolean(this.failedImages[this.storeKey(store, index)]);
    },
    markImageFailed(store, index) {
      this.$set(this.failedImages, this.storeKey(store, index), true);
    }
  }
};
</script>

<style lang="less" scoped>
.stores-page {
  min-height: 100vh;
  padding-top: 88px;
  box-sizing: border-box;
  background: #f4f8ff;
  color: #070d3f;

  ::v-deep .van-nav-bar__title {
    color: #070d3f;
    font-weight: 500;
  }

  ::v-deep .van-nav-bar .van-icon {
    color: #070d3f;
  }

  ::v-deep .van-pull-refresh__track {
    min-height: calc(100vh - 88px);
  }

  ::v-deep .van-pull-refresh__text {
    font-size: 24px;
    line-height: 34px;
    font-weight: 400;
    color: #64748B;
  }
}

.stores-page__content {
  min-height: calc(100vh - 88px);
  padding: 16px 16px calc(32px + env(safe-area-inset-bottom));
  box-sizing: border-box;
}

.store-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.store-card {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px;
  background: #ffffff;
  border-radius: 20px;
  box-sizing: border-box;
}

.store-card__image {
  flex: 0 0 176px;
  width: 176px;
  height: 176px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 20px;
  background: #f8faff;
  color: #9ca3af;

  img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
  }

  .van-icon {
    font-size: 72px;
  }
}

.store-card__details {
  min-width: 0;
  flex: 1;

  h2 {
    margin: 0;
    color: #070d3f;
    font-size: 26px;
    line-height: 36px;
    font-weight: 600;
    display: -webkit-box;
    overflow: hidden;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
}

.status-pill {
  display: inline-flex;
  align-items: center;
  margin-top: 8px;
  padding: 6px 14px;
  border-radius: 999px;
  background: #fff7ed;
  color: #ff7a00;
  font-size: 22px;
  line-height: 28px;
  font-weight: 500;

  &--open {
    background: #eaf8f0;
    color: #12c978;
  }
}

.store-info-row {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 16px;
  margin-top: 8px;
  color: #6b7280;
  text-decoration: none;
  box-sizing: border-box;

  &:first-of-type {
    margin-top: 16px;
    align-items: flex-start;
  }

  &--phone {
    color: #0b45ff;
  }
}

.store-info-row__icon {
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  background: #eaf3ff;
  color: #0b45ff;
  font-size: 26px;
}

.store-info-row__text {
  min-width: 0;
  flex: 1;
  font-size: 24px;
  line-height: 32px;
  font-weight: 500;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  &--address {
    white-space: normal;
    display: -webkit-box;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;
  }
}

.page-state {
  min-height: calc(100vh - 152px);
  padding: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  box-sizing: border-box;

  h2 {
    margin: 24px 0 0;
    color: #070d3f;
    font-size: 36px;
    line-height: 44px;
    font-weight: 600;
  }

  p {
    max-width: 640px;
    margin: 16px 0 0;
    color: #6b7280;
    font-size: 26px;
    line-height: 36px;
    font-weight: 400;
  }
}

.page-state--loading {
  gap: 16px;

  p {
    margin: 0;
  }
}

.page-state__icon {
  width: 112px;
  height: 112px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #fff1f1;
  color: #ff2d16;
  font-size: 56px;
}

.empty-illustration {
  width: 440px;
  height: 440px;
  display: block;
}

.primary-button {
  min-width: 400px;
  height: 92px;
  margin-top: 60px;
  padding: 0 40px;
  border: 0;
  border-radius: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: #0b45ff;
  color: #ffffff;
  font-size: 28px;
  line-height: 40px;
  font-weight: 500;
}

@media (max-width: 340px) {
  .store-card__image {
    flex-basis: 152px;
    width: 152px;
    height: 152px;
  }

  .store-card,
  .store-info-row {
    gap: 12px;
  }
}
</style>
